import {
  adminSettingsRef,
  ownerRef,
  publicRef,
} from '@/backend/firebase/services/helpers/firestore-refs'; // Ajusta si tu ruta es diferente
import type { Owner, OwnerWithId, Public } from '@/shared/schema';
import { getDoc, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import type { DeepPartial } from 'react-hook-form';

export const OwnerService = {
  // ... (upgradeToBusiness omitted for brevity in replace instruction, but it's kept)

  getById: async (uid: string): Promise<OwnerWithId | null> => {
    const ref = ownerRef(uid);
    const settingsRef = adminSettingsRef(uid);

    const [snap, settingsSnap] = await Promise.all([
      getDoc(ref),
      getDoc(settingsRef),
    ]);

    if (!snap.exists()) return null;

    const ownerData = snap.data() as OwnerWithId;

    // Merge the new admin settings into the profile for backward compatibility
    if (settingsSnap.exists()) {
      ownerData.subscription = settingsSnap.data() as any;
    }

    return ownerData;
  },

  update: async (
    uid: string,
    data: Partial<Omit<Owner, 'id' | 'audit' | 'account_type'>>,
  ) => {
    const docRef = ownerRef(uid);

    const updatePayload: Record<string, any> = {
      ...data,
      'audit.updatedAt': serverTimestamp(),
      'audit.updatedBy': uid,
    };

    try {
      await updateDoc(docRef, updatePayload);
      return true;
    } catch (error) {
      console.error('[OwnerService:update] ❌ Error al actualizar:', error);
      throw error;
    }
  },
  flattenObject: (obj: any, prefix = '', res: Record<string, any> = {}) => {
    for (const key in obj) {
      if (obj[key] === undefined) continue;

      const propName = prefix ? `${prefix}.${key}` : key;
      if (
        typeof obj[key] === 'object' &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        OwnerService.flattenObject(obj[key], propName, res);
      } else {
        res[propName] = obj[key];
      }
    }
    return res;
  },
  removeUndefinedDeep: (value: any): any => {
    if (value === undefined) return undefined;
    if (Array.isArray(value)) {
      return value
        .map((item) => OwnerService.removeUndefinedDeep(item))
        .filter((item) => item !== undefined);
    }
    if (value && typeof value === 'object') {
      return Object.entries(value).reduce<Record<string, any>>(
        (acc, [key, item]) => {
          const cleaned = OwnerService.removeUndefinedDeep(item);
          if (cleaned !== undefined) acc[key] = cleaned;
          return acc;
        },
        {},
      );
    }
    return value;
  },

  updatePublic: async (uid: string, publicData: DeepPartial<Public>) => {
    try {
      const sanitizedPublicData = OwnerService.removeUndefinedDeep(publicData);
      const flattened = OwnerService.flattenObject(
        sanitizedPublicData,
        'public',
      );
      await updateDoc(ownerRef(uid), flattened);
      await setDoc(publicRef(uid), sanitizedPublicData, { merge: true });
    } catch (error) {
      console.error(`[OwnerService] ❌ Error en el servicio:`, error);
      throw error;
    }
  },
};
